"use client";

import { useState, useEffect } from "react";

const AddPlan = ({
  savePlansCustomization,
  agencyPlans,
  selectedPlan,
  isEditMode,
  isDuplicate,
  resetPlanState,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedAgencyPlan, setSelectedAgencyPlan] = useState(null);
  const [customizationName, setCustomizationName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showPlanDropdown, setShowPlanDropdown] = useState(false);

  useEffect(() => {
    if ((isEditMode || isDuplicate) && selectedPlan) {
      setOpen(true);

      if (isEditMode) {
        setCustomizationName(selectedPlan.name || "");
      }

      const matchedPlan = agencyPlans.find(
        (plan) => plan.value === selectedPlan.planId
      ) || {
        value: selectedPlan.planId,
        label: selectedPlan.planName,
      };

      setSelectedAgencyPlan(matchedPlan);
      if (isDuplicate) {
        setSelectedAgencyPlan(null);
      }
    }
  }, [isEditMode, isDuplicate, selectedPlan, agencyPlans]);

  const handleOpen = () => {
    if (!isEditMode) {
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedAgencyPlan(null);
    setCustomizationName("");
    setSearchTerm("");
    setShowPlanDropdown(false);
    resetPlanState();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!customizationName.trim()) {
      alert("Customization name is required");
      return;
    }
    if (!selectedAgencyPlan) {
      alert("Please select a plan");
      return;
    }

    const data = {
      name: customizationName.trim(),
      plan: selectedAgencyPlan,
    };
    savePlansCustomization(data);
    handleClose();
  };

  const handlePlanSelect = (plan) => {
    setSelectedAgencyPlan(plan);
    setShowPlanDropdown(false);
    setSearchTerm("");
  };

  const filteredPlans = agencyPlans.filter((plan) =>
    plan.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const title = isEditMode
    ? "Edit plan customization"
    : isDuplicate
    ? "Duplicate plan customization"
    : "Add new plan customization";

  const submitLabel = isEditMode
    ? "Update customization"
    : isDuplicate
    ? "Duplicate customization"
    : "Save customization";

  return (
    <>
      {!isEditMode && (
        <button
          type="button"
          className="ap-trigger d-inline-flex align-items-center"
          onClick={handleOpen}
        >
          <span className="ap-trigger-icon d-inline-flex align-items-center justify-content-center" aria-hidden="true">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </span>
          <span>Add new group</span>
        </button>
      )}

      {open && (
        <div className="ap-overlay" onMouseDown={handleClose}>
          <div
            className="ap-dialog"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <form onSubmit={handleSubmit}>
              <header className="ap-header d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="ap-title mb-0">{title}</h5>
                  <p className="ap-subtitle mb-0">
                    Map a SaaS plan to a UI configuration.
                  </p>
                </div>
                <button
                  type="button"
                  className="ap-close"
                  onClick={handleClose}
                  aria-label="Close"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </header>

              <div className="ap-body">
                <div className="ap-field">
                  <label className="ap-label">Customization name</label>
                  <input
                    type="text"
                    className="ap-input"
                    placeholder="e.g. Pro Plan layout"
                    value={customizationName}
                    onChange={(e) => setCustomizationName(e.target.value)}
                    required
                  />
                </div>

                <div className="ap-field mb-0">
                  <label className="ap-label d-flex justify-content-between align-items-center">
                    <span>Plan</span>
                    {selectedAgencyPlan && (
                      <span className="ap-selected-pill">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        Selected
                      </span>
                    )}
                  </label>

                  <div className="ap-search-wrap position-relative">
                    <span className="ap-search-icon d-flex align-items-center justify-content-center" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="7" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                    </span>
                    <input
                      type="text"
                      className="ap-input ap-input--search"
                      value={
                        searchTerm !== "" || !selectedAgencyPlan
                          ? searchTerm
                          : selectedAgencyPlan.label
                      }
                      placeholder="Search plans"
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onFocus={() => setShowPlanDropdown(true)}
                    />
                    <button
                      type="button"
                      className="ap-search-toggle"
                      onClick={() => setShowPlanDropdown(!showPlanDropdown)}
                      aria-label="Toggle dropdown"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                          transform: showPlanDropdown ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.18s ease",
                        }}
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>

                    {showPlanDropdown && (
                      <div className="ap-dropdown">
                        {filteredPlans.length === 0 ? (
                          <div className="ap-dropdown-empty">
                            {searchTerm ? "No plans match your search." : "No available plans."}
                          </div>
                        ) : (
                          filteredPlans.map((plan) => {
                            const isCurrent = selectedAgencyPlan?.value === plan.value;
                            return (
                              <button
                                key={plan.value}
                                type="button"
                                className={`ap-dropdown-item d-flex align-items-center justify-content-between ${isCurrent ? "is-current" : ""}`}
                                onClick={() => handlePlanSelect(plan)}
                              >
                                <span className="text-truncate">{plan.label}</span>
                                {isCurrent ? (
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="ap-dropdown-check">
                                    <polyline points="20 6 9 17 4 12" />
                                  </svg>
                                ) : (
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ap-dropdown-arrow">
                                    <polyline points="9 18 15 12 9 6" />
                                  </svg>
                                )}
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <footer className="ap-footer d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="ap-btn ap-btn--ghost"
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="ap-btn ap-btn--primary"
                  disabled={!selectedAgencyPlan || !customizationName.trim()}
                >
                  {submitLabel}
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}

      <style>{`
        /* Trigger */
        .ap-trigger {
          position: relative;
          font-family: 'Inter', sans-serif;
          font-size: 0.8125rem;
          font-weight: 600;
          letter-spacing: 0.005em;
          padding: 8px 16px 8px 8px;
          border-radius: 9px;
          border: 1px solid rgba(255, 255, 255, 0.04);
          line-height: 1;
          cursor: pointer;
          gap: 8px;
          color: #fff;
          background: linear-gradient(180deg, #6366f1 0%, #4f46e5 100%);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 1px 2px rgba(79, 70, 229, 0.25),
            0 6px 14px rgba(79, 70, 229, 0.22);
          transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease;
        }
        .ap-trigger:hover {
          background: linear-gradient(180deg, #4f46e5 0%, #4338ca 100%);
          transform: translateY(-1px);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 2px 4px rgba(67, 56, 202, 0.3),
            0 10px 22px rgba(79, 70, 229, 0.32);
        }
        .ap-trigger:hover .ap-trigger-icon {
          background: rgba(255, 255, 255, 0.26);
          transform: rotate(90deg);
        }
        .ap-trigger:active {
          transform: translateY(0);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 1px 2px rgba(67, 56, 202, 0.3);
        }
        .ap-trigger:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2), 0 6px 14px rgba(79, 70, 229, 0.28);
        }
        .ap-trigger-icon {
          width: 22px;
          height: 22px;
          border-radius: 6px;
          background: rgba(255, 255, 255, 0.18);
          color: #fff;
          flex-shrink: 0;
          transition: background 0.2s ease, transform 0.2s ease;
        }

        /* Overlay & dialog */
        .ap-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.45);
          backdrop-filter: blur(2px);
          z-index: 1050;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          font-family: 'Inter', sans-serif;
          animation: apFade 0.18s ease;
        }
        @keyframes apFade { from { opacity: 0; } to { opacity: 1; } }
        .ap-dialog {
          background: #fff;
          border-radius: 14px;
          border: 1px solid #ececec;
          width: 100%;
          max-width: 540px;
          max-height: calc(100vh - 48px);
          display: flex;
          flex-direction: column;
          box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
          animation: apPop 0.18s ease;
          overflow: hidden;
        }
        @keyframes apPop {
          from { opacity: 0; transform: scale(0.97) translateY(6px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .ap-header { padding: 18px 22px 14px; }
        .ap-title {
          font-size: 1.0625rem;
          font-weight: 600;
          color: #1f2937;
          letter-spacing: -0.005em;
        }
        .ap-subtitle {
          font-size: 0.8125rem;
          color: #6b7280;
          margin-top: 2px !important;
        }
        .ap-close {
          width: 30px;
          height: 30px;
          border-radius: 8px;
          background: transparent;
          border: 0;
          color: #6b7280;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.15s ease, color 0.15s ease;
        }
        .ap-close:hover { background: #f3f4f6; color: #1f2937; }
        .ap-body { padding: 6px 22px 18px; overflow-y: auto; }
        .ap-footer {
          padding: 14px 22px 18px;
          border-top: 1px solid #f1f1f1;
          background: #fff;
        }

        /* Fields */
        .ap-field { margin-bottom: 16px; }
        .ap-label {
          display: block;
          font-size: 0.8125rem;
          font-weight: 500;
          color: #1f2937;
          margin-bottom: 6px;
        }
        .ap-selected-pill {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 0.6875rem;
          font-weight: 500;
          color: #4f46e5;
          background: #eef2ff;
          border: 1px solid #e0e7ff;
          padding: 2px 8px;
          border-radius: 999px;
        }
        .ap-input {
          width: 100%;
          height: 38px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 0 12px;
          background: #fff;
          font-size: 0.875rem;
          color: #1f2937;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
          font-family: inherit;
        }
        .ap-input::placeholder { color: #9ca3af; }
        .ap-input:focus {
          outline: none;
          border-color: #4f46e5;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
        }
        .ap-input--search { padding-left: 36px; padding-right: 36px; }
        .ap-search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
          pointer-events: none;
        }
        .ap-search-toggle {
          position: absolute;
          right: 6px;
          top: 50%;
          transform: translateY(-50%);
          width: 28px;
          height: 28px;
          border: 0;
          background: transparent;
          color: #6b7280;
          border-radius: 6px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.15s ease, color 0.15s ease;
        }
        .ap-search-toggle:hover { background: #f3f4f6; color: #1f2937; }

        /* Dropdown */
        .ap-dropdown {
          position: absolute;
          top: calc(100% + 6px);
          left: 0;
          right: 0;
          background: #fff;
          border: 1px solid #ececec;
          border-radius: 10px;
          box-shadow: 0 8px 24px rgba(17, 24, 39, 0.08);
          max-height: 220px;
          overflow-y: auto;
          z-index: 1060;
          padding: 4px;
          animation: apDropFade 0.14s ease;
        }
        @keyframes apDropFade {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .ap-dropdown-empty {
          padding: 14px;
          text-align: center;
          font-size: 0.8125rem;
          color: #9ca3af;
        }
        .ap-dropdown-item {
          width: 100%;
          padding: 9px 10px;
          font-size: 0.875rem;
          color: #1f2937;
          background: transparent;
          border: 0;
          border-radius: 7px;
          text-align: left;
          cursor: pointer;
          transition: background 0.12s ease, color 0.12s ease;
        }
        .ap-dropdown-item:hover {
          background: #f5f3ff;
          color: #4338ca;
        }
        .ap-dropdown-item.is-current {
          background: #eef2ff;
          color: #4338ca;
          font-weight: 600;
        }
        .ap-dropdown-arrow {
          color: #9ca3af;
          flex-shrink: 0;
          transition: color 0.12s ease, transform 0.12s ease;
        }
        .ap-dropdown-item:hover .ap-dropdown-arrow {
          color: #4f46e5;
          transform: translateX(2px);
        }
        .ap-dropdown-check {
          color: #4f46e5;
          flex-shrink: 0;
        }

        /* Buttons */
        .ap-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8125rem;
          font-weight: 600;
          letter-spacing: 0.005em;
          padding: 8px 16px;
          border-radius: 9px;
          border: 1px solid transparent;
          line-height: 1;
          cursor: pointer;
          font-family: inherit;
          transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease;
        }
        .ap-btn:disabled { opacity: 0.55; cursor: not-allowed; transform: none !important; }
        .ap-btn:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2), 0 4px 10px rgba(79, 70, 229, 0.18);
        }
        .ap-btn--ghost {
          background: #fff;
          border-color: #e5e7eb;
          color: #1f2937;
          box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
        }
        .ap-btn--ghost:hover:not(:disabled) {
          background: #fafafa;
          border-color: #d1d5db;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(17, 24, 39, 0.05), 0 6px 14px rgba(17, 24, 39, 0.06);
        }
        .ap-btn--ghost:active:not(:disabled) { transform: translateY(0); }

        .ap-btn--primary {
          background: linear-gradient(180deg, #6366f1 0%, #4f46e5 100%);
          color: #fff;
          border-color: rgba(255, 255, 255, 0.04);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 1px 2px rgba(79, 70, 229, 0.22),
            0 4px 10px rgba(79, 70, 229, 0.2);
        }
        .ap-btn--primary:hover:not(:disabled) {
          background: linear-gradient(180deg, #4f46e5 0%, #4338ca 100%);
          transform: translateY(-1px);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 2px 4px rgba(67, 56, 202, 0.28),
            0 8px 18px rgba(79, 70, 229, 0.3);
        }
        .ap-btn--primary:active:not(:disabled) {
          transform: translateY(0);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 1px 2px rgba(67, 56, 202, 0.25);
        }
      `}</style>
    </>
  );
};

export default AddPlan;
